import { useMemo, useState, type FormEvent } from "react";

import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestoreIcon from "@mui/icons-material/Restore";
import SendIcon from "@mui/icons-material/Send";
import SecurityIcon from "@mui/icons-material/Security";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import {
  type AdminUser,
  type UserRoleAssignment,
  addUserRole,
  createUser,
  listUserRoles,
  listUsers,
  removeUserRole,
  resendInvite,
  updateUser,
  updateUserRole,
} from "@/api/users";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";

const TENANT_ROLES = ["TENANT_ADMIN"];
const ORG_ROLES = ["ORG_ADMIN", "COORDINATOR", "DOCTOR", "VIEWER", "AUDITOR"];

const STATUS_COLOR: Record<
  string,
  "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error"
> = {
  ACTIVE: "success",
  INVITED: "info",
  SUSPENDED: "error",
  ARCHIVED: "default",
};

export function UsersAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canCreate = usePermission("user:create");
  const canRead = usePermission("user:read");

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "ACTIVE" | "INVITED" | "SUSPENDED">(
    "",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [rolesFor, setRolesFor] = useState<AdminUser | null>(null);
  const canManageRoles = usePermission("user:update");
  /** Email of the last invite the server could not actually send, if any. */
  const [undeliveredTo, setUndeliveredTo] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ["admin-users", q, statusFilter],
    queryFn: () =>
      listUsers({
        q: q || undefined,
        status: (statusFilter || undefined) as
          | "ACTIVE"
          | "INVITED"
          | "SUSPENDED"
          | undefined,
        limit: 100,
      }),
    enabled: canRead,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ACTIVE" | "SUSPENDED" }) =>
      updateUser(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: (user) => {
      setUndeliveredTo(user.invite_email_sent === false ? user.email : null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  if (!canRead) {
    return (
      <Box>
        <PageHeader title={t("users.title")} subtitle={t("users.subtitle")} helpTerm="utilisateur" />
        <Alert severity="warning">{t("users.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        actions={
          <>
            <IconButton onClick={() => users.refetch()} aria-label={t("common.retry")}>
              <RefreshIcon />
            </IconButton>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                {t("users.invite")}
              </Button>
            )}
          </>
        }
      />

      {undeliveredTo && (
        <Alert
          severity="warning"
          onClose={() => setUndeliveredTo(null)}
          sx={{ mb: 2 }}
        >
          {t("users.inviteNotDelivered", { email: undeliveredTo })}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label={t("common.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t("users.statusFilter")}</InputLabel>
          <Select
            label={t("users.statusFilter")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <MenuItem value="">{t("users.allStatuses")}</MenuItem>
            <MenuItem value="ACTIVE">{t("users.status.ACTIVE")}</MenuItem>
            <MenuItem value="INVITED">{t("users.status.INVITED")}</MenuItem>
            <MenuItem value="SUSPENDED">{t("users.status.SUSPENDED")}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {users.isLoading ? (
        <CircularProgress />
      ) : (users.data?.items ?? []).length === 0 ? (
        <Alert severity="info">{t("users.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("users.col.email")}</TableCell>
                <TableCell>{t("users.col.name")}</TableCell>
                <TableCell>{t("users.col.status")}</TableCell>
                <TableCell align="center">{t("users.col.mfa")}</TableCell>
                <TableCell align="right" sx={{ minWidth: 220 }}>
                  {t("users.col.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(users.data?.items ?? []).map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onResend={() => resendMutation.mutate(u.id)}
                  onSuspend={() => updateMutation.mutate({ id: u.id, status: "SUSPENDED" })}
                  onReactivate={() => updateMutation.mutate({ id: u.id, status: "ACTIVE" })}
                  onManageRoles={canManageRoles ? () => setRolesFor(u) : undefined}
                  busy={updateMutation.isPending || resendMutation.isPending}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <InviteDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(user) => {
          setCreateOpen(false);
          setUndeliveredTo(user.invite_email_sent === false ? user.email : null);
          qc.invalidateQueries({ queryKey: ["admin-users"] });
        }}
      />

      <UserRolesDialog user={rolesFor} onClose={() => setRolesFor(null)} />
    </Box>
  );
}


function UserRow({
  user,
  onResend,
  onSuspend,
  onReactivate,
  onManageRoles,
  busy,
}: {
  user: AdminUser;
  onResend: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
  onManageRoles?: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {user.email}
        </Typography>
        {user.is_god && (
          <Chip size="small" color="secondary" label="GOD" sx={{ mt: 0.5 }} />
        )}
      </TableCell>
      <TableCell>{user.full_name ?? "—"}</TableCell>
      <TableCell>
        <Chip
          size="small"
          color={STATUS_COLOR[user.status] ?? "default"}
          label={t(`users.status.${user.status}`)}
        />
        {user.has_pending_invite && (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={t("users.invitePending")}
            sx={{ ml: 0.5 }}
          />
        )}
      </TableCell>
      <TableCell align="center">
        {user.mfa_enabled ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {onManageRoles && !user.is_god && (
          <Tooltip title={t("users.roles.manage")}>
            <span>
              <IconButton size="small" onClick={onManageRoles} disabled={busy}>
                <SecurityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {user.status === "INVITED" && (
          <Tooltip title={t("users.resendInvite")}>
            <span>
              <IconButton size="small" onClick={onResend} disabled={busy}>
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {user.status === "ACTIVE" && !user.is_god && (
          <Tooltip title={t("users.suspend")}>
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={onSuspend}
                disabled={busy}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {user.status === "SUSPENDED" && (
          <Tooltip title={t("users.reactivate")}>
            <span>
              <IconButton
                size="small"
                color="success"
                onClick={onReactivate}
                disabled={busy}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}


/** Add or remove role assignments on an existing user.
 *
 * A user may legitimately hold several — typically TENANT_ADMIN over the whole
 * tenant plus DOCTOR on each organisation they actually work in. Changing the
 * set revokes the target's sessions server-side, so they must sign in again.
 */
function UserRolesDialog({
  user,
  onClose,
}: {
  user: AdminUser | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [roleCode, setRoleCode] = useState("DOCTOR");
  const [orgId, setOrgId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const userId = user?.id ?? null;

  const roles = useQuery({
    queryKey: ["admin-user-roles", userId],
    queryFn: () => listUserRoles(userId as string),
    enabled: Boolean(userId),
  });

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
    enabled: Boolean(userId),
  });

  const needsOrg = useMemo(() => !TENANT_ROLES.includes(roleCode), [roleCode]);

  const onMutationError = (e: unknown) => {
    const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
    setError(detail || "error");
  };
  const refresh = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: ["admin-user-roles", userId] });
  };

  const addMutation = useMutation({
    mutationFn: () =>
      addUserRole(userId as string, {
        role_code: roleCode,
        org_id: needsOrg ? orgId : null,
      }),
    onSuccess: () => {
      setOrgId("");
      refresh();
    },
    onError: onMutationError,
  });

  const removeMutation = useMutation({
    mutationFn: (assignmentId: string) => removeUserRole(userId as string, assignmentId),
    onSuccess: refresh,
    onError: onMutationError,
  });

  // Retargeting in place, rather than remove-then-add, keeps the user from
  // being briefly role-less and keeps one audit line instead of two.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState("DOCTOR");
  const [editOrg, setEditOrg] = useState("");
  const editNeedsOrg = useMemo(() => !TENANT_ROLES.includes(editRole), [editRole]);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateUserRole(userId as string, editingId as string, {
        role_code: editRole,
        org_id: editNeedsOrg ? editOrg : null,
      }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: onMutationError,
  });

  const startEdit = (a: UserRoleAssignment) => {
    setError(null);
    setEditingId(a.id);
    setEditRole(a.role_code);
    setEditOrg(a.scope === "ORG" ? (a.scope_id ?? "") : "");
  };

  const onSaveEdit = () => {
    setError(null);
    if (editNeedsOrg && !editOrg) {
      setError("org_id_required_for_role");
      return;
    }
    updateMutation.mutate();
  };

  const onAdd = () => {
    setError(null);
    if (needsOrg && !orgId) {
      setError("org_id_required_for_role");
      return;
    }
    addMutation.mutate();
  };

  const busy =
    addMutation.isPending || removeMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={Boolean(user)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("users.roles.title")}
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error">
              {t(`users.err.${error}`, { defaultValue: error })}
            </Alert>
          )}

          {roles.isLoading ? (
            <CircularProgress size={24} />
          ) : (roles.data ?? []).length === 0 ? (
            <Alert severity="warning">{t("users.roles.empty")}</Alert>
          ) : (
            <List dense disablePadding>
              {(roles.data ?? []).map((a) =>
                editingId === a.id ? (
                  <ListItem key={a.id} divider sx={{ display: "block", py: 1.5 }}>
                    <Stack spacing={1.5}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>{t("users.role")}</InputLabel>
                        <Select
                          label={t("users.role")}
                          value={editRole}
                          onChange={(e) => setEditRole(e.target.value)}
                        >
                          <MenuItem value="TENANT_ADMIN">
                            {t("users.role_label.TENANT_ADMIN")}
                          </MenuItem>
                          {ORG_ROLES.map((r) => (
                            <MenuItem key={r} value={r}>
                              {t(`users.role_label.${r}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {editNeedsOrg && (
                        <FormControl size="small" fullWidth>
                          <InputLabel>{t("users.organization")}</InputLabel>
                          <Select
                            label={t("users.organization")}
                            value={editOrg}
                            onChange={(e) => setEditOrg(e.target.value)}
                          >
                            {(orgs.data?.items ?? []).map((o) => (
                              <MenuItem key={o.id} value={o.id}>
                                {o.code} — {o.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" onClick={onSaveEdit} disabled={busy}>
                          {t("common.save")}
                        </Button>
                        <Button size="small" onClick={() => setEditingId(null)} disabled={busy}>
                          {t("common.cancel")}
                        </Button>
                      </Stack>
                    </Stack>
                  </ListItem>
                ) : (
                  <ListItem
                    key={a.id}
                    divider
                    secondaryAction={
                      <>
                        <Tooltip title={t("users.roles.edit")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => startEdit(a)}
                              disabled={busy}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("users.roles.remove")}>
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => removeMutation.mutate(a.id)}
                              disabled={busy}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    }
                  >
                    <ListItemText
                      primary={t(`users.role_label.${a.role_code}`, {
                        defaultValue: a.role_label,
                      })}
                      secondary={
                        a.scope === "ORG"
                          ? `${t("users.roles.scope.ORG")} · ${a.scope_label ?? a.scope_id}`
                          : a.scope === "TENANT"
                            ? `${t("users.roles.scope.TENANT")} · ${a.scope_label ?? ""}`
                            : t("users.roles.scope.GOD")
                      }
                    />
                  </ListItem>
                ),
              )}
            </List>
          )}

          <Divider />

          <Typography variant="overline" color="text.secondary">
            {t("users.roles.addTitle")}
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>{t("users.role")}</InputLabel>
            <Select
              label={t("users.role")}
              value={roleCode}
              onChange={(e) => setRoleCode(e.target.value)}
            >
              <MenuItem value="TENANT_ADMIN">{t("users.role_label.TENANT_ADMIN")}</MenuItem>
              {ORG_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`users.role_label.${r}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {needsOrg && (
            <FormControl size="small" fullWidth>
              <InputLabel>{t("users.organization")}</InputLabel>
              <Select
                label={t("users.organization")}
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              >
                {(orgs.data?.items ?? []).map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.code} — {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAdd}
            disabled={busy}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("users.roles.add")}
          </Button>

          <Alert severity="info">{t("users.roles.reloginHint")}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}


function InviteDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (user: AdminUser) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roleCode, setRoleCode] = useState("DOCTOR");
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  const needsOrg = useMemo(() => !TENANT_ROLES.includes(roleCode), [roleCode]);
  const orgLabels = new Map(
    (orgs.data?.items ?? []).map((o) => [o.id, `${o.code} — ${o.name}`]),
  );

  const mutation = useMutation({
    mutationFn: () =>
      createUser({
        email: email.trim().toLowerCase(),
        full_name: fullName.trim(),
        role_code: roleCode,
        org_ids: needsOrg ? orgIds : [],
      }),
    onSuccess: (user) => {
      setEmail("");
      setFullName("");
      setRoleCode("DOCTOR");
      setOrgIds([]);
      setError(null);
      onCreated(user);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response
        ?.data?.detail;
      setError(detail || "error");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !fullName) {
      setError("required_fields_missing");
      return;
    }
    if (needsOrg && orgIds.length === 0) {
      setError("org_id_required_for_role");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("users.invite")}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error">
                {t(`users.err.${error}`, { defaultValue: error })}
              </Alert>
            )}
            <TextField
              label={t("users.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              size="small"
              autoComplete="off"
            />
            <TextField
              label={t("users.fullName")}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t("users.role")}</InputLabel>
              <Select
                label={t("users.role")}
                value={roleCode}
                onChange={(e) => setRoleCode(e.target.value)}
              >
                <MenuItem value="TENANT_ADMIN">{t("users.role_label.TENANT_ADMIN")}</MenuItem>
                {ORG_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(`users.role_label.${r}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {needsOrg && (
              <FormControl size="small" fullWidth>
                <InputLabel>{t("users.organizations")}</InputLabel>
                <Select
                  multiple
                  label={t("users.organizations")}
                  value={orgIds}
                  onChange={(e) =>
                    setOrgIds(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                    )
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip key={id} size="small" label={orgLabels.get(id) ?? id} />
                      ))}
                    </Box>
                  )}
                >
                  {(orgs.data?.items ?? []).map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      <Checkbox size="small" checked={orgIds.includes(o.id)} />
                      <ListItemText primary={`${o.code} — ${o.name}`} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t("users.organizationsHint")}</FormHelperText>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary">
              {t("users.inviteHint")}
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {t("users.sendInvite")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
